<template>
  <div>
    <EmptyState
      v-if="!formattedRevs.length && !isLoading"
      class="py-8 px-16"
      :icon="'mdi-bee'"
      :iconSize="48"
      :title="$t('profile.empty-revs-title')"
      :message="emptyStateMessage"
      :buttonSubtitle="
        userRole === 'owner' && isSelfProfile
          ? $t('profile.empty-revs-subtitle-self-profile')
          : null
      "
      :buttonText="
        userRole === 'owner' && isSelfProfile ? $t('common.create_rev') : null
      "
      :buttonTestId:="'profile-revTable-create-rev-button'"
      :buttonTourId="'profile-revTable-create-rev-button'"
      @click="showCreateModal = true"
    />
    <v-sheet v-else>
      <v-data-table
        id="data-user-revs"
        data-private
        calculate-widths
        :headers="headers"
        :items="formattedRevs"
        :options.sync="options"
        :footer-props="footerOptions"
        :server-items-length="totalRecordsInData"
        :loading="isLoading"
      >
        <template v-slot:item.name="{ item }">
          <router-link
            class="router-link"
            data-testid="revsSection-routerLink-revName"
            :to="getRoute(item.revType, item.id)"
            @click.native="trackView(item)"
          >
            <p class="ma-0 pa-0 text-decoration-none blue--text">
              {{ item.name }}
            </p>
          </router-link>
        </template>

        <template v-slot:item.status="{ item }">
          <RevStatus :rev="item" showValidationIcons />
          <v-tooltip top>
            <template v-slot:activator="{ on }" v-if="item.isHighlighted">
              <v-icon color="amber lighten-2" class="ms-2" v-on="on"
                >mdi-star</v-icon
              >
            </template>
            {{ $t('rev.status.highlighted') }}
          </v-tooltip>
        </template>
      </v-data-table>
    </v-sheet>
    <CreateContentModal
      v-if="showCreateModal"
      :show="showCreateModal"
      @close="showCreateModal = false"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import revMixin from '@/mixins/revMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import RevStatus from '@/components/Rev/RevStatus.vue'
import CreateContentModal from '@/components/CreateContentModal/CreateContentModal.vue'

import EmptyState from '@/components/EmptyState.vue'
export default {
  name: 'RevsTable',

  mixins: [mixin, revMixin, trackerMixin],

  components: {
    RevStatus,
    CreateContentModal,
    EmptyState,
  },

  props: {
    organizationId: {
      type: String,
      required: true,
    },
    accountId: {
      type: String,
      required: true,
    },
    isSelfProfile: {
      required: true,
      type: Boolean,
    },
    filterSelected: {
      type: Object,
    },
  },

  data() {
    return {
      revs: [],
      currentPage: 1,
      DEFAULT_SIZE: 20,
      totalRecordsInData: 0,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      isLoading: true,
      showCreateModal: false,
      headers: [
        {
          text: this.$t('common.name'),
          value: 'name',
          align: 'start',
          width: 160,
        },
        {
          text: this.$t('common.types'),
          value: 'revTypeName',
          align: 'start',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
          align: 'start',
        },
        {
          text: this.$t('common.roles.coach_one'),
          value: 'coach',
          align: 'start',
        },
        {
          text: this.$t('common.date.date'),
          value: 'createdAt',
          align: 'start',
        },
      ],
      userRole: 'owner',
      lastRequestKey: null,
      requestSequence: 0,
    }
  },

  watch: {
    accountId(oldVal, newVal) {
      if (oldVal === newVal) return
      this.setRevsByRole(this.options)
    },

    filterSelected(newVal, oldVal) {
      if (oldVal === newVal) return
      this.userRole = newVal.value
      this.setRevsByRole(this.options)
    },
  },

  mounted() {
    this.setRevsByRole(this.options)
  },

  computed: {
    ...mapGetters(['organization']),

    formattedRevs() {
      return this.revs.map(rev => {
        return {
          id: rev.id,
          name: rev.name,
          coach: this.getFullName(rev.coach),
          status: this.getRevStatus(rev),
          revType: rev.entityType,
          revTypeName:
            this.organization?.customNamings?.[rev.entityType]?.singular ||
            this.$t(`common.${_.snakeCase(rev.entityType)}_one`),
          createdAt: utils.dates.humanDate(rev.createdAt),
          validations: rev.validations,
          isHighlighted: rev.isHighlighted,
        }
      })
    },

    options: {
      get() {
        return {
          itemsLength: this.totalRecordsInData,
          itemsPerPage: this.DEFAULT_SIZE,
          multiSort: false,
          mustSort: false,
          page: 1,
        }
      },
      set(event) {
        this.setRevsByRole({
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: _.isEmpty(event.sortBy) ? true : event.sortDesc[0],
          sortBy: event.sortBy[0] ?? 'createdAt',
        })
      },
    },

    totalPages: function() {
      return Math.ceil(this.totalRecordsInData / this.DEFAULT_SIZE)
    },

    emptyStateMessage() {
      let message

      if (!this.isSelfProfile) {
        message = this.$t('profile.empty-revs-subtitle')
      } else if (this.userRole !== 'owner') {
        message = this.$t('profile.empty-revs-subtitle-self-profile')
      } else {
        message = null
      }

      return message
    },
  },

  methods: {
    ...mapActions({
      getRevsByUserRole: 'profile/getRevsByUserRole',
    }),

    getRoute(revType, revId) {
      if (revType === 'improvementIdea') {
        return `/rev/${this.organizationId}/${revId}`
      }

      return `/${_.kebabCase(revType)}/${this.organizationId}/${revId}`
    },

    async setRevsByRole(options) {
      const size = options?.itemsPerPage ?? this.DEFAULT_SIZE
      const paginationOptions = {
        limit: size,
        from: options?.page * size - size,
        sort: {
          by: options?.sortBy ?? 'createdAt',
          order: options?.sortDesc === false ? 'asc' : 'desc',
        },
        projections: [
          'name',
          'type',
          'status',
          'coach',
          'condition',
          'createdAt',
          'validations',
          'isHighlighted',
        ],
        expansions: ['coach'],
      }

      const requestKey = JSON.stringify({
        role: this.userRole,
        accountId: this.accountId,
        limit: paginationOptions.limit,
        from: paginationOptions.from,
        sort: paginationOptions.sort,
      })

      if (requestKey === this.lastRequestKey) return
      this.lastRequestKey = requestKey
      this.requestSequence += 1
      const requestId = this.requestSequence

      try {
        this.isLoading = true

        const response = await this.getRevsByUserRole({
          organizationId: this.organizationId,
          role: this.userRole,
          accountId: this.accountId,
          options: paginationOptions,
        })

        if (requestId !== this.requestSequence) return

        this.revs = response?.results ?? []
        this.totalRecordsInData = response?.totalRecordsInData ?? 0
      } catch (error) {
        if (requestId !== this.requestSequence) return
        this.lastRequestKey = null
        this.handleRequestError(error)
      } finally {
        if (requestId === this.requestSequence) this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.empty-state-subtitle {
  margin: 0px 35%;
}
.v-application a.router-link {
  color: $color-blue-grey-darken3;
  text-decoration: none;
}
</style>
